// Status media, fetched by ID against our own origin (D8, ADR-0023 D2).
//
// The `AvatarLoader` shape, and for the same three reasons: the fetch needs
// the bearer so an `<img src>` cannot do it; the server decides per viewer, so
// a 404 is a legitimate answer meaning "expired, taken down, or not for you"
// indistinguishably; and the view builder is pure, so it needs a synchronous
// `peek` that kicks off the load and repaints when it lands.
//
// What differs from the avatar loader is the sweep. An avatar is fetched once
// and drawn for months; a status is gone in 24 hours, so `sweepExpired` drops
// the blobs of statuses the tray no longer holds — a cache that outlived the
// thing it caches would make "gone in 24 hours" false on the one screen where
// somebody would notice.

export type StatusRendition = "full" | "thumb" | "poster" | "video" | "voice";

export interface StatusMediaRef {
  statusId: string;
  rendition: StatusRendition;
}

/**
 * The single URL builder. A port of KMP's `StatusMediaUrl`, byte for byte:
 * nothing else in the web shell may compose a status media URL.
 */
export function statusMediaUrl(apiBase: string, ref: StatusMediaRef): string {
  const base = apiBase.replace(/\/+$/, "");
  return `${base}/api/v1/statuses/${encodeURIComponent(ref.statusId)}/media?r=${ref.rendition}`;
}

const MISS_TTL_MS = 5 * 60 * 1000;
const MAX_ENTRIES = 48;

export class StatusMediaLoader {
  /** key → blob: URL, in insertion (≈ LRU) order. */
  private blobs = new Map<string, string>();
  private misses = new Map<string, number>();
  private inflight = new Map<string, Promise<string | null>>();

  private readonly apiBase: string;
  private readonly bearer: () => string | null;
  /** Called when a blob URL becomes available, so the shell can repaint. */
  private readonly onLoaded: () => void;
  private readonly doFetch: typeof fetch;

  constructor(
    apiBase: string,
    bearer: () => string | null,
    onLoaded: () => void,
    doFetch: typeof fetch = (...args) => fetch(...args),
  ) {
    this.apiBase = apiBase;
    this.bearer = bearer;
    this.onLoaded = onLoaded;
    this.doFetch = doFetch;
  }

  private key(ref: StatusMediaRef): string {
    return `${ref.statusId}/${ref.rendition}`;
  }

  /** The blob: URL if this loader already holds one — synchronous, for the
   *  pure view builder. Kicks off a fetch when it holds nothing. */
  peek(ref: StatusMediaRef): string | null {
    const key = this.key(ref);
    const hit = this.blobs.get(key);
    if (hit) {
      this.blobs.delete(key);
      this.blobs.set(key, hit);
      return hit;
    }
    const missedAt = this.misses.get(key);
    if (missedAt !== undefined && Date.now() < missedAt + MISS_TTL_MS) return null;
    void this.load(ref);
    return null;
  }

  async load(ref: StatusMediaRef): Promise<string | null> {
    const key = this.key(ref);
    const existing = this.blobs.get(key);
    if (existing) return existing;
    const flying = this.inflight.get(key);
    if (flying) return flying;

    const p = this.fetchBlob(ref, key);
    this.inflight.set(key, p);
    try {
      return await p;
    } finally {
      this.inflight.delete(key);
    }
  }

  private async fetchBlob(ref: StatusMediaRef, key: string): Promise<string | null> {
    const token = this.bearer();
    if (!token) return null;
    let res: Response;
    try {
      res = await this.doFetch(statusMediaUrl(this.apiBase, ref), {
        headers: { authorization: `Bearer ${token}`, accept: "*/*" },
      });
    } catch {
      return null; // network hiccup: not a miss worth remembering
    }
    if (!res.ok) {
      this.misses.set(key, Date.now());
      return null;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    this.remember(key, url);
    this.onLoaded();
    return url;
  }

  private remember(key: string, url: string): void {
    this.blobs.set(key, url);
    while (this.blobs.size > MAX_ENTRIES) {
      const oldest = this.blobs.keys().next().value as string;
      const victim = this.blobs.get(oldest);
      this.blobs.delete(oldest);
      if (victim) URL.revokeObjectURL(victim);
    }
  }

  /**
   * Drop the blobs of statuses that are gone.
   *
   * `liveIds` is what the tray still holds. Everything else names a status
   * that has expired, been deleted or been taken down, and the whole promise
   * of this feature is that those stop existing here too.
   */
  sweepExpired(liveIds: Set<string>): void {
    for (const [key, url] of [...this.blobs]) {
      const id = key.slice(0, key.lastIndexOf("/"));
      if (!liveIds.has(id)) {
        this.blobs.delete(key);
        URL.revokeObjectURL(url);
      }
    }
    for (const key of [...this.misses.keys()]) {
      const id = key.slice(0, key.lastIndexOf("/"));
      if (!liveIds.has(id)) this.misses.delete(key);
    }
  }

  /** After a block or a privacy change. */
  forgetMisses(): void {
    this.misses.clear();
  }

  /** Sign-out: drop and revoke everything. */
  clear(): void {
    for (const url of this.blobs.values()) URL.revokeObjectURL(url);
    this.blobs.clear();
    this.misses.clear();
  }
}
